import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { alpha } from '@mui/material/styles';
import App from './App';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

// Calm, neutral theme: graphite surfaces, one muted accent, small radii,
// compact controls by default.
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#d27a5a', light: '#e39a7f', dark: '#b36146', contrastText: '#16120f' },
    secondary: { main: '#c9b38a' },
    background: { default: '#121212', paper: '#1a1a1a' },
    text: { primary: '#ebe7e1', secondary: '#a19c94', disabled: '#6b6761' },
    divider: 'rgba(235,231,225,0.09)',
    action: { hover: 'rgba(235,231,225,0.05)' },
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 13.5,
    h1: { fontWeight: 600, letterSpacing: '-0.025em' },
    h2: { fontWeight: 600, letterSpacing: '-0.02em' },
    h3: { fontWeight: 600, letterSpacing: '-0.02em' },
    h4: { fontWeight: 600, letterSpacing: '-0.015em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    button: { fontWeight: 500, textTransform: 'none' },
    overline: { fontWeight: 500, letterSpacing: '0.08em' },
  },
  shape: { borderRadius: 6 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 6 } },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { border: '1px solid rgba(235,231,225,0.09)', borderRadius: 8 },
      },
    },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: '1rem', fontWeight: 600 } } },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiIconButton: { defaultProps: { size: 'small' } },
    MuiTooltip: { defaultProps: { arrow: false }, styleOverrides: { tooltip: { fontSize: '0.72rem' } } },
    MuiMenu: { styleOverrides: { paper: { border: '1px solid rgba(235,231,225,0.09)' } } },
  },
});

// Some MUI X components call `theme.alpha(...)`. Ensure it's available
// for older MUI versions by providing a small shim that delegates to the
// `alpha` utility.
theme.alpha = (color, opacity) => alpha(color, opacity);

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
